import { supabase } from '../lib/supabase';
import auditLogService from './auditLogService';

const dataPrivacyService = {
  /**
   * Get privacy preferences for user
   * @param {string} userId - User ID
   */
  async getPrivacyPreferences(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('privacy_preferences')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;

      return { data: data?.privacy_preferences || this.getDefaultPrivacyPreferences(), error: null };
    } catch (error) {
      console.error('Error fetching privacy preferences:', error);
      return { data: null, error };
    }
  },

  /**
   * Update privacy preferences
   * @param {string} userId - User ID
   * @param {object} preferences - Privacy preferences object
   */
  async updatePrivacyPreferences(userId, preferences) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({ 
          privacy_preferences: preferences,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', userId)
        ?.select('privacy_preferences')
        ?.single();

      if (error) throw error;

      // Log privacy settings change
      await auditLogService?.createLog(
        userId,
        auditLogService?.EVENT_TYPES?.SECURITY_SETTINGS_CHANGE,
        'Privacy preferences updated',
        {
          success: true,
          metadata: {
            settingsType: 'privacy_preferences',
            timestamp: new Date()?.toISOString()
          }
        }
      );

      return { data: data?.privacy_preferences, error: null };
    } catch (error) {
      console.error('Error updating privacy preferences:', error);
      return { data: null, error };
    }
  },

  /**
   * Verify encryption status
   * @param {string} userId - User ID
   */
  async verifyEncryptionStatus(userId) {
    try {
      const { data, error } = await supabase?.rpc('verify_encryption_status', {
        p_user_id: userId
      });

      if (error) throw error;

      // Log encryption verification
      await auditLogService?.createLog(
        userId,
        auditLogService?.EVENT_TYPES?.SECURITY_SETTINGS_CHANGE,
        'Encryption status verified',
        {
          success: true,
          metadata: {
            verificationType: 'encryption',
            timestamp: new Date()?.toISOString()
          }
        }
      );

      return { data, error: null };
    } catch (error) {
      console.error('Error verifying encryption:', error);
      return { data: null, error };
    }
  },

  /**
   * Get storage optimization metrics
   * @param {string} userId - User ID
   */
  async getStorageMetrics(userId) {
    try {
      const { data, error } = await supabase?.rpc('calculate_storage_metrics', {
        p_user_id: userId
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching storage metrics:', error);
      return { data: null, error };
    }
  },

  /**
   * Get default privacy preferences
   */
  getDefaultPrivacyPreferences() {
    return {
      encryption: {
        atRestEnabled: true,
        inTransitEnabled: true,
        certificateValidUntil: null,
        lastVerified: null
      },
      dataResidency: {
        region: 'US',
        migrationStatus: 'completed',
        complianceCertifications: ['SOC2', 'GDPR']
      },
      piiMasking: {
        emailAddresses: true,
        phoneNumbers: true,
        paymentInfo: true,
        auditTrailEnabled: true
      },
      retentionPolicy: {
        conversations: 90,
        tasks: 365,
        auditLogs: 730,
        analyticsData: 180,
        autoDeleteEnabled: true
      }
    };
  },

  /**
   * Get available data residency regions
   */
  getAvailableRegions() {
    return [
      { value: 'US', label: 'United States', description: 'US East (Virginia)' },
      { value: 'EU', label: 'European Union', description: 'EU West (Ireland)' },
      { value: 'APAC', label: 'Asia Pacific', description: 'APAC Southeast (Singapore)' }
    ];
  },

  /**
   * Get retention period options
   */
  getRetentionPeriodOptions() {
    return [
      { value: 30, label: '30 days' },
      { value: 60, label: '60 days' },
      { value: 90, label: '90 days' },
      { value: 180, label: '180 days' },
      { value: 365, label: '1 year' },
      { value: 730, label: '2 years' }
    ];
  },

  /**
   * Format date for display
   * @param {string} dateString - ISO date string
   */
  formatDate(dateString) {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  },

  /**
   * Calculate days until expiration
   * @param {string} expirationDate - ISO date string
   */
  getDaysUntilExpiration(expirationDate) {
    if (!expirationDate) return null;
    const expiry = new Date(expirationDate);
    const now = new Date();
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }
};

export default dataPrivacyService;
